// src/components/sections/OpportunityHeader.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { getStatusColor } from '../../utils/colors';
import { formatDate } from '../../utils/constants';

const getPOVScoreColor = (score) => {
  if (score === null || score === undefined) return '#757575';
  if (score >= 7.5) return '#03DAC6';
  if (score >= 4)   return '#FFCF44';
  return '#CF6679';
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const OpportunityHeader = ({ clientName, stage, expectedValue, expectedCloseDate, startDate, reviScore }) => {
  const formattedCloseDate = expectedCloseDate ? formatDate(expectedCloseDate) : 'TBD';
  const formattedStartDate = startDate ? formatDate(startDate) : 'TBD';

  return (
    <Box sx={{ p: 1.5, bgcolor: '#1A1A1A', display: 'flex', flexDirection: 'column', gap: 0.75 }}>

      {/* Row 1 — client name + stage/POV chips */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#03DAC6', fontWeight: 'bold' }}>
          {clientName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={stage}
            size="small"
            sx={{ fontSize: '0.65rem', bgcolor: `${getStatusColor(stage)}30`, color: getStatusColor(stage), height: 20 }}
          />
          {typeof reviScore === 'number' && (
            <Chip
              label={`POV©: ${reviScore}`}
              size="small"
              sx={{ fontSize: '0.65rem', bgcolor: `${getPOVScoreColor(reviScore)}20`, color: getPOVScoreColor(reviScore), height: 20, fontWeight: 'bold' }}
            />
          )}
        </Box>
      </Box>

      {/* Row 2 — Expected Value hero metric */}
      <Box>
        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', letterSpacing: '0.5px', textTransform: 'uppercase', mb: 0.25 }}>
          Expected Value
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
          {formatCurrency(expectedValue)}
        </Typography>
      </Box>

      {/* Row 3 — dates */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <EventIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: '#64B5F6' }} />
        <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
          Close: {formattedCloseDate}{startDate && ` · Start: ${formattedStartDate}`}
        </Typography>
      </Box>

    </Box>
  );
};

OpportunityHeader.propTypes = {
  clientName: PropTypes.string.isRequired,
  stage: PropTypes.string,
  expectedValue: PropTypes.number,
  expectedCloseDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  reviScore: PropTypes.number
};

export default OpportunityHeader;
